import cn from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useScrollNavigation } from 'src/shared/hooks/useScrollNavigation';
import { DishModalContent } from './DishModalContent';

const NAV_HEIGHT = 54;

type TabName = 'DISH INFO' | 'INGREDIENTS' | 'NUTRITION' | 'REVIEWS' | 'SHEF BIO';
const TAB_NAMES: TabName[] = ['DISH INFO', 'INGREDIENTS', 'NUTRITION', 'REVIEWS', 'SHEF BIO'];
type ButtonData = { element: HTMLDivElement; title: TabName };

interface DishModalNavProps {
  dishInfoRef: HTMLDivElement | null;
  ingredientsRef: HTMLDivElement | null;
  nutritionRef: HTMLDivElement | null;
  reviewsRef: HTMLDivElement | null;
  shefBioRef: HTMLDivElement | null;
  modalContainerRef: React.RefObject<HTMLDivElement>;
  dishNameRef: HTMLParagraphElement | null;
  headerRef: HTMLDivElement | null;
}

const NavButton: React.FC<{
  element: HTMLDivElement;
  isActive?: boolean;
  title: TabName;
  onClick: (itemId: TabName) => void;
}> = ({ element, title, isActive, onClick }) => {
  if (!element) return null;

  const handleClick = () => {
    onClick(title);
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        'whitespace-nowrap rounded-lg border-2 border-eggplant-50 p-2 pb-1.5 text-caption-600 uppercase',
        'hover:border-eggplant-200 active:border-eggplant-500 active:bg-eggplant-50',
        {
          'bg-eggplant-500 text-white': isActive,
          'bg-white text-eggplant-500': !isActive,
        }
      )}>
      {title}
    </button>
  );
};

export const DishModalNav: React.FC<DishModalNavProps> = ({
  dishInfoRef,
  ingredientsRef,
  nutritionRef,
  reviewsRef,
  shefBioRef,
  modalContainerRef,
  dishNameRef,
  headerRef,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Calculate header height when nav is visible (nav is only visible when header is visible)
  useEffect(() => {
    if (isVisible && headerRef) {
      setHeaderHeight(headerRef.getBoundingClientRect().height);
    }
  }, [isVisible, headerRef]);

  const buttonsToRender = useMemo(
    () =>
      [
        { element: dishInfoRef, title: TAB_NAMES[0] },
        { element: ingredientsRef, title: TAB_NAMES[1] },
        { element: nutritionRef, title: TAB_NAMES[2] },
        { element: reviewsRef, title: TAB_NAMES[3] },
        { element: shefBioRef, title: TAB_NAMES[4] },
      ].filter((item): item is ButtonData => !!item.element),
    [dishInfoRef, ingredientsRef, nutritionRef, reviewsRef, shefBioRef]
  );

  // Create navigation items for useScrollNavigation
  const navigationItems = useMemo(() => buttonsToRender.map(({ title }) => ({ id: title })), [buttonsToRender]);

  // Custom nav height calculator for container scrolling
  // Use header visibility to determine nav height - if header is visible, nav should be below it
  const navHeightCalculator = useMemo(
    () => (_navElement: HTMLDivElement | null) => headerHeight + NAV_HEIGHT,
    [headerHeight]
  );

  // Use the hook with container scrolling support
  const {
    activeItemId: activeTab,
    setItemRef,
    setNavElement,
    handleItemClick,
  } = useScrollNavigation({
    items: navigationItems,
    navHeightCalculator,
    scrollContainer: modalContainerRef.current,
  });

  // Connect the existing refs to the hook
  useMemo(() => {
    buttonsToRender.forEach(({ element, title }) => {
      setItemRef(title, element);
    });
  }, [buttonsToRender, setItemRef]);

  // Show nav when dish title has scrolled past where the nav would appear
  // Nav appears at headerHeight and has height NAV_HEIGHT
  useEffect(() => {
    const modalContainer = modalContainerRef.current;
    if (!modalContainer || !dishNameRef || !headerRef) return;

    const handleScroll = (): void => {
      const nameRect = dishNameRef.getBoundingClientRect();
      const currentHeaderRect = headerRef.getBoundingClientRect();
      const currentNavBottomPosition = currentHeaderRect.bottom + NAV_HEIGHT;
      const hasScrolled = modalContainer.scrollTop > 0;
      const shouldShow = nameRect.top < currentNavBottomPosition;
      setIsVisible(hasScrolled && shouldShow);
    };

    modalContainer.addEventListener('scroll', handleScroll);

    return () => {
      modalContainer.removeEventListener('scroll', handleScroll);
    };
  }, [modalContainerRef, dishNameRef, headerRef]);

  const combinedNavRef = useMemo(
    () => (el: HTMLDivElement | null) => {
      setNavElement(el);
      if (el && headerHeight > 0) {
        el.style.setProperty('--header-height', `${headerHeight}px`);
      }
    },
    [setNavElement, headerHeight]
  );

  return (
    <div
      ref={combinedNavRef}
      className={cn('top-45 absolute left-0 z-10 bg-rice transition-all', {
        'pointer-events-none h-0 overflow-hidden opacity-0': !isVisible,
        'opacity-100': isVisible,
        'top-[45px]': isVisible,
      })}>
      <DishModalContent className='no-scrollbar relative flex h-[54px] w-full items-center overflow-x-scroll'>
        <div className='flex gap-2 bg-rice pr-2'>
          {buttonsToRender.map(({ element, title }, index) => (
            <NavButton
              key={`nav-button-${index}`}
              title={title}
              element={element}
              isActive={title === activeTab}
              onClick={handleItemClick}
            />
          ))}
        </div>
      </DishModalContent>
    </div>
  );
};
